// ParametrosLaboralesModal.jsx
import { useEffect, useState } from 'react';
import { ENDPOINTS } from '../../../Utils/Endpoints';
import { Models } from '../../../Utils/ModelsCarBooking';
import api from '../../../../../api';

const EMPTY = {
  idUnidadOrganizacional: 0,
  topeSemanalMin: 0,
  topeMensualMin: 0,
  fechaCreacion: '',
  activo: true,
};

const endpointUnidadesOrg = ENDPOINTS.unidadesOrgSecurity;
const modeloUnidadesOrg = Models.unidadesOrg;

export default function Modal({
  open,
  mode = 'new',
  data = null,
  onClose,
  onSubmit,
  inline = false,
}) {
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({
    idUnidadOrganizacional: '',
    topeSemanalMin: '',
    topeMensualMin: '',
  });
  const [unidadesOrg, setUnidadesOrg] = useState([]);
  const [loadingUo, setLoadingUo] = useState(false);

  const validateField = (field, rawValue) => {
    const value = typeof rawValue === 'string' ? rawValue.trim() : rawValue;
    let error = '';

    switch (field) {
      case 'idUnidadOrganizacional':
        if (!value || Number(value) <= 0) error = 'Selecciona una unidad organizacional.';
        break;
      case 'topeSemanalMin':
        if (value === '' || value === null || value === undefined) {
          error = 'El tope semanal es obligatorio.';
        } else if (Number(value) < 0) {
          error = 'El tope semanal no puede ser negativo.';
        }
        break;
      case 'topeMensualMin':
        if (value === '' || value === null || value === undefined) {
          error = 'El tope mensual es obligatorio.';
        } else if (Number(value) < 0) {
          error = 'El tope mensual no puede ser negativo.';
        } else if (Number(value) < 4 * Number(form.topeSemanalMin)) {
          error = 'El tope mensual no puede ser menor a 4 veces el tope semanal.';
        }
        break;
      default:
        break;
    }

    setErrors((prev) => ({ ...prev, [field]: error }));
    return !error;
  };

  const validateAll = () => {
    const fields = ['idUnidadOrganizacional', 'topeSemanalMin', 'topeMensualMin'];
    let isValid = true;
    fields.forEach(f => {
      if (!validateField(f, form[f])) isValid = false;
    });
    return isValid;
  };

  const handleChange = (field, value) => {
    setForm((prev) => {
      const next = { ...prev, [field]: value };

      // Sincronización inteligente: Si el nuevo tope semanal x 4 supera al mensual actual,
      // actualizamos el mensual automáticamente para cumplir la regla.
      if (field === 'topeSemanalMin' && value !== '') {
        const minMensual = Number(value) * 4;
        if (Number(prev.topeMensualMin) < minMensual) {
          next.topeMensualMin = minMensual;
        }
      }

      return next;
    });
    validateField(field, value);

    // Si cambia el tope semanal, re-validamos el mensual por la regla de "4 veces"
    if (field === 'topeSemanalMin') {
      setTimeout(() => {
        setForm(prev => {
          validateField('topeMensualMin', prev.topeMensualMin);
          return prev;
        });
      }, 0);
    }
  };

  // Cargar catálogo de Unidades Organizacionales
  useEffect(() => {
    const loadUnidadesOrg = async () => {
      try {
        setLoadingUo(true);
        // Solo permitimos seleccionar unidades de la empresa propia (propias),
        // excluyendo las sucursales (hijas) del select para evitar configuraciones cruzadas.
        const res = await api.get(endpointUnidadesOrg('propias'));
        const norm = (res.data || []).map((x) => modeloUnidadesOrg(x));
        setUnidadesOrg(norm);
      } catch (e) {
        console.error(e);
        // sin alert para no meter validaciones/molestias extra
      } finally {
        setLoadingUo(false);
      }
    };

    if (open) {
      loadUnidadesOrg();
    }
  }, [open]);

  // Cargar datos de edición / limpiar en modo "new"
  useEffect(() => {
    if (mode === 'edit' && data) {
      const filled = {
        idParametro: data.idParametro ?? 0,
        idUnidadOrganizacional: data.idUnidadOrganizacional ?? 0,
        topeSemanalMin: data.topeSemanalMin ?? 0,
        topeMensualMin: data.topeMensualMin ?? 0,
        fechaCreacion: data.fechaCreacion ?? '',
        activo: data.activo ?? true,
      };
      setForm(filled);
      validateField('idUnidadOrganizacional', filled.idUnidadOrganizacional);
      validateField('topeSemanalMin', filled.topeSemanalMin);
      validateField('topeMensualMin', filled.topeMensualMin);
    } else {
      setForm(EMPTY);
      setErrors({ idUnidadOrganizacional: '', topeSemanalMin: '', topeMensualMin: '' });
    }
  }, [mode, data]);

  if (!open) return null;

  const hasErrors = Object.values(errors).some((e) => !!e);
  const isSaveDisabled =
    !form.idUnidadOrganizacional ||
    form.topeSemanalMin === '' ||
    form.topeSemanalMin === null ||
    form.topeMensualMin === '' ||
    form.topeMensualMin === null ||
    hasErrors;

  const errorStyle = {
    color: '#b91c1c',
    fontSize: '.8rem',
    display: 'block',
    marginTop: '4px',
  };

  // helper para mostrar nombre bonito de la unidad
  const getUnidadNombre = (uo) => {
    if (!uo) return '';
    const base =
      (uo.nombreUnidadOrganizacional ||
        uo.nombreUnidadOrg ||
        uo.nombre ||
        uo.descripcion ||
        ''
      ).toString();
    return uo.clave ? `${base} (${uo.clave})` : base;
  };

  const Header = (
    <div
      className="roles-topbar"
      style={{
        marginBottom: '0.6rem',
        paddingRight: 16,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flex: '0 0 auto',
      }}
    >
      <h3 className="roles-title" style={{ marginRight: 0 }}>
        {mode === 'new' ? 'Nuevo parámetro laboral' : 'Editar parámetro laboral'}
      </h3>
      <button className="roles-btn" onClick={onClose} aria-label="Cerrar">
        ✕
      </button>
    </div>
  );

  const Body = (
    <div
      style={{
        paddingLeft: '12%',
        paddingRight: 16,
        display: 'grid',
        gridTemplateColumns: '2fr 1fr 1fr',
        gap: '0.8rem',
        overflowY: 'auto',
        flex: '1 1 auto',
        minHeight: 0,
      }}
    >
      <div>
        <label className="roles-title" style={{ fontSize: '.95rem' }}>
          Unidad Organizacional:
        </label>
        <br />
        <br />
        <select
          className={`infoField${errors.idUnidadOrganizacional ? ' infoField--error' : ''}`}
          value={form.idUnidadOrganizacional ?? 0}
          onChange={(e) => handleChange('idUnidadOrganizacional', e.target.value === '' ? 0 : Number(e.target.value))}
          disabled={loadingUo}
        >
          <option value={0}>
            {loadingUo ? 'Cargando unidades…' : '-- Selecciona una unidad --'}
          </option>
          {unidadesOrg.map((uo) => (
            <option
              key={uo.idUnidadOrganizacional}
              value={uo.idUnidadOrganizacional}
            >
              {getUnidadNombre(uo)}
            </option>
          ))}
        </select>
        {errors.idUnidadOrganizacional && <small style={errorStyle}>{errors.idUnidadOrganizacional}</small>}
      </div>

      <div>
        <label className="roles-title" style={{ fontSize: '.95rem' }}>
          Tope semanal (Horas):
        </label>
        <br />
        <br />
        <input
          className={`infoField${errors.topeSemanalMin ? ' infoField--error' : ''}`}
          type="number"
          min={0}
          value={form.topeSemanalMin}
          onChange={(e) => handleChange('topeSemanalMin', e.target.value === '' ? '' : Number(e.target.value))}
        />
        {errors.topeSemanalMin && <small style={errorStyle}>{errors.topeSemanalMin}</small>}
      </div>

      <div>
        <label className="roles-title" style={{ fontSize: '.95rem' }}>
          Tope mensual (Horas):
        </label>
        <br />
        <br />
        <input
          className={`infoField${errors.topeMensualMin ? ' infoField--error' : ''}`}
          type="number"
          min={0}
          value={form.topeMensualMin}
          onChange={(e) => handleChange('topeMensualMin', e.target.value === '' ? '' : Number(e.target.value))}
        />
        {errors.topeMensualMin && <small style={errorStyle}>{errors.topeMensualMin}</small>}
      </div>


    </div>
  );

  const Footer = (
    <div
      style={{
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '.6rem',
        marginTop: '.8rem',
        paddingRight: 16,
        flex: '0 0 auto',
      }}
    >
      <button className="roles-btn" onClick={onClose}>
        Cancelar
      </button>
      <button
        className="btn-action"
        onClick={() => {
          if (validateAll()) onSubmit(form, mode);
        }}
        disabled={isSaveDisabled}
        style={{
          opacity: isSaveDisabled ? 0.5 : 1,
          cursor: isSaveDisabled ? 'not-allowed' : 'pointer',
        }}
      >
        {mode === 'new' ? 'Guardar' : 'Actualizar'}
      </button>
    </div>
  );

  if (inline) {
    return (
      <div
        className="roles-inline-card"
        style={{ display: 'flex', flexDirection: 'column', gap: 0 }}
      >
        {Header}
        {Body}
        {Footer}
      </div>
    );
  }

  return (
    <div
      className="roles-confirm-overlay"
      role="dialog"
      aria-modal="true"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15,23,42,.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
        zIndex: 1000,
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div
        className="roles-confirm-card"
        style={{
          width: 'min(900px, 96vw)',
          maxHeight: '85vh',
          background: '#fff',
          borderRadius: 12,
          boxShadow: '0 10px 30px rgba(0,0,0,.25)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {Header}
        {Body}
        {Footer}
      </div>
    </div>
  );
}
